import { useEffect, useState } from "react";
import { SITE, formatDate } from "@/lib/site";

interface CiteThisProps {
  title: string;
  url: string; // full canonical URL
  author?: string;
  publishedDate?: string | null;
}

type Format = "link" | "markdown" | "html" | "apa" | "bibtex";

const FORMAT_LABELS: { id: Format; label: string }[] = [
  { id: "link", label: "Plain link" },
  { id: "markdown", label: "Markdown" },
  { id: "html", label: "HTML" },
  { id: "apa", label: "APA" },
  { id: "bibtex", label: "BibTeX" },
];

function buildCitation(fmt: Format, p: CiteThisProps): string {
  const author = p.author ?? `${SITE.name} Newsroom`;
  const year = p.publishedDate ? new Date(p.publishedDate).getUTCFullYear() : new Date().getUTCFullYear();
  const date = p.publishedDate ? formatDate(p.publishedDate) : formatDate(new Date().toISOString());
  switch (fmt) {
    case "link":
      return p.url;
    case "markdown":
      return `[${p.title} — ${SITE.name}](${p.url})`;
    case "html":
      return `<a href="${p.url}">${p.title} — ${SITE.name}</a>`;
    case "apa":
      return `${author}. (${year}, ${date.split(",")[0]}). ${p.title}. ${SITE.name}. ${p.url}`;
    case "bibtex": {
      const key = (p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 32)) || "entry";
      return `@misc{mapobserver-${key}-${year},\n  title  = {${p.title}},\n  author = {${author}},\n  year   = {${year}},\n  url    = {${p.url}},\n  note   = {${SITE.name}, accessed ${date}}\n}`;
    }
  }
}

export function CiteThis(props: CiteThisProps) {
  const [open, setOpen] = useState(false);
  const [fmt, setFmt] = useState<Format>("link");
  const [copied, setCopied] = useState(false);

  const value = buildCitation(fmt, props);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // ignore
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-paper-soft px-3 py-1.5 text-xs uppercase tracking-wider text-foreground/80 hover:bg-foreground hover:text-background transition-colors"
        aria-label={`Cite ${props.title}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M6 8h4v8H4V12c0-2.2 0-3.3 .6-4Z" /><path d="M16 8h4v8h-6V12c0-2.2 0-3.3 .6-4Z" />
        </svg>
        Cite this page
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Cite this page"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-xl rounded-sm border border-border bg-background p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Cite this page</p>
                <h2 className="mt-1 headline-display text-xl leading-tight">{props.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {FORMAT_LABELS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFmt(f.id)}
                  className={`rounded-sm border px-3 py-1 text-xs uppercase tracking-wider transition-colors ${
                    fmt === f.id
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-foreground/80 hover:bg-paper-soft"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <pre className="mt-4 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-border bg-paper-soft p-3 text-xs leading-relaxed font-mono">
              {value}
            </pre>
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Free to use with attribution. Help us grow into the authority for local search.
              </p>
              <button
                type="button"
                onClick={copy}
                className="rounded-sm border border-foreground bg-foreground px-4 py-2 text-xs uppercase tracking-wider text-background hover:bg-accent hover:border-accent transition-colors"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
